<template>
    <section class="topup">
        <wallet-form :title="$t('buyChz.howManyChzBuy')"
            :isButtonDisabled="isButtonDisabled"
            :balance="balance"
            :isButtonShow="true"
            :isSidebarVisible="false"
            :buttonText="$t('buyChz.proceedToPay')"
            :class="['topup', {'topup--disabled': isIframeShown}]"
            :showMalaysianDeclarationLabel="true"
            @proceedButtonAction="proceedButtonAction"
            @setMalaysianDomesticRinggitBorrowingModal="setMalaysianDomesticRinggitBorrowingModal"
        >
            <div class="topup__available-balance">
                <span class="text--bold">{{ $t('buyChz.availableBalance') }}</span>
                <span>{{ availableChzBalanceText }}</span>
            </div>

            <div class="topup__overlay"
                @click="unlockForm"
            >
                <i class="icon-edit" />
                <span>{{ $t('buyChz.editAmount') }}</span>
            </div>

            <div class="topup__inputs topup__inputs--dark">
                <token-list id="partner-tile-fiat"
                    :activeItem="fiatCurrency"
                    :tokensList="!isTurned ? formattedFiatCurrenciesList : []"
                    @selectToken="onSelectFiatCurrency"
                />

                <div class="topup__input-wrapper flex flex--column flex--justify-flex-end">
                    <input id="input-fiat"
                        v-autofocus
                        :class="['topup__input', { 'topup__input--invalid': isTopupLimitReached }]"
                        type="number"
                        placeholder="0"
                        :value="fiatValue.formatted"
                        @input="calculateValue($event, isCurrencySwitched)"
                    >

                    <div v-if="isTopupLimitReached"
                        class="daily-limit-reached flex flex--justify-flex-end flex--align-center"
                    >
                        <span class="daily-limit-reached__text">{{ $t('topup.dailyLimitReached') }}</span>
                        <i class="icon icon-info"
                            @click="onClickDailyLimitReachedInfo"
                        />
                    </div>
                </div>
            </div>

            <div class="topup__middle-row">
                <i id="currency-swapper"
                    :class="['icon', 'icon-exchange', {'icon-exchange--twirl' : isTurned}]"
                    @click="switchCurrenciesAction"
                />

                <exchange-rate :conversionRateInfo="conversionRateInfo" />
            </div>

            <div class="topup__inputs">
                <token-list id="partner-tile-crypto"
                    :activeItem="cryptoCurrency"
                    :tokensList="isTurned ? formattedFiatCurrenciesList : []"
                    @selectToken="onSelectFiatCurrency"
                />

                <input id="input-crypto"
                    class="topup__input"
                    type="text"
                    placeholder="150"
                    :value="cryptoValue.formatted"
                    disabled
                >
            </div>
        </wallet-form>

        <wallet-form v-if="isIframeShown"
            :header="$t('buyChz.paymentProcess')"
            :isSidebarVisible="false"
            class="payment-iframe"
        >
            <iframe id="webApp_iframe"
                :src="iframeSrc"
                frameborder="0"
                name="webApp_iframe"
                class="webApp_iframe"
                allow="clipboard-write *; camera"
            />
        </wallet-form>
    </section>
</template>

<script>
// 3rd party
import {
    mapGetters,
    mapActions,
} from 'vuex';

import axios from 'axios';

import {
    addMinutes,
    compareAsc,
} from '@shared/utils/dates';

// Components
import walletForm from '@webapp/components/wallet/wallet-form.vue';
import exchangeRate from '@webapp/components/exchange-rate/exchange-rate.vue';
import tokenList from '@webapp/components/token-tiles/token-list.vue';

// Helpers
import apiUrl from '@webapp/helpers/api-url.js';
import {
    scrollToElement,
} from '@webapp/helpers/generic.js';
import {
    goBack,
} from '@webapp/helpers/router';
import {
    formatPrice,
} from '@shared/utils/formatter.js';

// Config
import socios from '@webapp/socios.json';

// Config
import {
    PERMISSION_CODE,
} from '@shared/config/user-permissions';

// Mixins
import amountMixin from '@webapp/helpers/amount-mixin.js';
import walletMixin from '@shared/mixins/wallet-mixin.js';
import userRestrictionMixin from '@webapp/helpers/user-restriction-mixin';

// Local Types
import localEpicTypes from '@webapp/store/types.js';
import {
    TransactionType,
} from '@webapp/types/transaction';

// Shared Types
import sharedEpicTypes from '@shared/epics/types.js';

// Shared Modules
import {
    AdjustParameterKeys,
    EventNames,
} from '@shared/utils/analytics-keys';

import {
    RequirementType, RequirementId,
} from '@shared/types/jurisdiction';

import {
    askUserForFillRequirements,
} from '@shared/services/jurisdiction/requirements';

import {
    getPermissionRequirements,
} from '@shared/services/jurisdiction/requirements/check-permission';

export default {
    name: 'wallet-topup',
    components: {
        walletForm,
        exchangeRate,
        tokenList,
    },
    mixins: [
        amountMixin,
        walletMixin,
        userRestrictionMixin,
    ],
    props: {
        balance: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            mode: 'topup',
            currentRate: 0,
            currentRateDate: null,
            isCurrencySwitched: false,
            eurRate: 0,
            fiatToCrypto: {
                rate: null,
                date: null,
            },
            cryptoToFiat: {
                rate: null,
                date: null,
            },
            iframeSrc: '',
            isTurned: false,
            isIframeShown: false,
            modalAmount: '',
        };
    },
    computed: {
        ...mapGetters([
            'locale',
            'kycTierLevel',
            'userId',
            'userDetails',
            'currencyConversionRates',
            'getCurrencyConversion',
            'device',
            'hasRestrictedPayments',
            'hasTopupGeoRestricted',
            'isPartnerApp',
            'partnerAppData',
            'userId',
        ]),

        ...mapGetters('wallet', [
            'remainingLedgerCumulativeLimit',
            'centralizedWalletCHZ',
        ]),

        topupConversion() {
            return this.getCurrencyConversion(this.fiatCurrency.id, this.cryptoCurrency.id);
        },

        isButtonDisabled() {
            return (!Number(this.cryptoValue.formatted) && !Number(this.fiatCurrency.formatted)) ||
                this.isIframeShown ||
                this.isTopupLimitReached || this.isBuyTokenDenied;
        },

        availableChzBalanceText() {
            const {
                plainBalance = 0,
                currency: {
                    symbol: chzSymbol = '',
                } = {},
            } = this.centralizedWalletCHZ;

            const fiatBalance = formatPrice(plainBalance / this.originalFiatToCryptoRate);
            const fiatCurrencyName = this.isTurned ? this.cryptoCurrency.name : this.fiatCurrency.name;

            return `: ${formatPrice(plainBalance)} ${chzSymbol} (${fiatBalance} ${fiatCurrencyName})`;
        },
        adjustParams() {
            return {
                key: AdjustParameterKeys.USER_ID,
                value: this.userId,
            };
        },
        restrictionModal() {
            if (this.hasRestrictedPayments) {
                return 'components/modals/us-payment-restrictions-info';
            } else if (this.hasTopupGeoRestricted) {
                return 'components/modals/geo-restricted-topup-info';
            }

            return null;
        },
        isBuyTokenDenied() {
            const { deniedRequirementsReasons } = getPermissionRequirements(PERMISSION_CODE.BUY_TOKEN);

            return Boolean(deniedRequirementsReasons?.length);
        },
    },
    async mounted() {
        if (
            this.hasRestrictedPayments ||
            this.hasTopupGeoRestricted
        ) {
            this.setModal({
                isVisible: true,
                isClosable: false,
                componentToLoad: this.restrictionModal,
                data: {
                    ctaOpts: {
                        text: this.$t('global.button.goBack'),
                        callback: () => {
                            this.modalCallback();
                            this.resetModal();
                        },
                    },
                },
            });

            return;
        }

        this.showAppLoader({
            isVisible: true,
            hasOpacity: true,
        });

        await this.getWallet({
            chilizCurrencyId: socios.chilizCurrencyId,
        });

        await this.checkRegulatoryRestrictionLimit({
            transactionName: TransactionType.Purchase,
            isSendOutRegulatoryRestriction: false,
            withFetch: {
                doFetch: true,
            },
        });

        this.showAppLoader({
            isVisible: false,
        });
        this.eurRate = this.currentRate;
    },
    destroyed() {
        window.removeEventListener('message', this.receiveMessage);
    },
    methods: {
        ...mapActions({
            setModal: localEpicTypes.SET_MODAL,
            resetModal: localEpicTypes.RESET_MODAL,
            showAppLoader: localEpicTypes.SET_APP_LOADER,
            getWallet: sharedEpicTypes.GET_WALLET,
        }),

        unlockForm() {
            if (this.isIframeShown) {
                this.isIframeShown = false;
            }
        },

        switchCurrenciesAction() {
            this.isTurned = !this.isTurned;
            this.switchCurrenciesAndConversionRate();
        },

        onClickDailyLimitReachedInfo() {
            const showButtons = false;

            this.setRegulatoryRestrictionModal(TransactionType.Purchase, showButtons);
        },

        onSelectFiatCurrency(currency) {
            // Once switched, the variables are reversed as well, so we need to cater for that
            if (this.isTurned) {
                this.cryptoCurrency = currency;
            } else {
                this.fiatCurrency = currency;
            }

            this.walletMixinRefreshCurrencyConversionRates(
                [this.fiatCurrency.id, this.cryptoCurrency.id],
                [this.cryptoCurrency.id, this.fiatCurrency.id]
            );
        },

        async getGeneratedCode() {
            let code = '';

            try {
                const { data: response } = await axios.post(`${apiUrl()}/session/auth-token`);

                if (response && response.data) {
                    code = response.data.code;
                }
            } catch (error) {
                console.error(error);
            }

            return code;
        },

        async proceedButtonAction() {
            let chzAmount = this.cryptoValue,
                fiatAmount = this.fiatValue,
                fiatCurrency = this.fiatCurrency;

            if (this.isTurned) {
                fiatAmount = this.cryptoValue;
                chzAmount = this.fiatValue;
                fiatCurrency = this.cryptoCurrency;
            }

            const amount = chzAmount.original;

            if (amount > 0) {
                const total = Number(amount);

                if (this.remainingLedgerCumulativeLimit !== null && (Number(total) > Number(this.remainingLedgerCumulativeLimit))) {
                    this.setModal({
                        isVisible: true,
                        componentToLoad: 'components/wallet/wallet-level-up-modal',
                        data: {
                            kycLevel: this.kycTierLevel,
                            type: 'topup',
                        },
                    });

                    return false;
                }

                // Add 15 minutes to the date returned from when getting the rate since rate expires in 15 minutes from when it is retrieved.
                const expiryRateDate = addMinutes(new Date(this.currentRateDate), 15);

                if (compareAsc(expiryRateDate, new Date()) === -1) {
                    this.isLoadingConversionRates = true;

                    const currenciesFromList = [this.fiatCurrency.id];
                    const currenciesToList = [this.cryptoCurrency.id];

                    await this.walletMixinRefreshCurrencyConversionRates(currenciesFromList, currenciesToList);

                    return false;
                }

                let isJSON = false;

                try {
                    isJSON = JSON.parse([this.device?.uuid])?.data;
                } catch (err) {
                    // Err is already controled with boolean in case user didnt accept cookies
                }

                const deviceId = isJSON ? isJSON : this.device?.uuid;

                this.openIframe({
                    fiatAmount,
                    chzAmount,
                    fiatCurrency,
                    deviceId,
                });

                const eurAmount = (chzAmount.formatted / this.eurRate).toFixed(2);

                // GA add_to_cart tracking
                this.$webTrackingInstance?.sendEvent(
                    EventNames.AddToCart,
                    [
                        {
                            key: 'currency',
                            value: 'EUR',
                        },
                        {
                            key: 'userId',
                            value: this.userId,
                        },
                        {
                            key: 'device_id',
                            value: deviceId,
                        },
                        {
                            key: 'amount',
                            value: eurAmount,
                        },
                    ]
                );

                // Trigger Adjust tracking event
                this.$webTrackingInstance?.sendEvent(
                    EventNames.TopupInitiated,
                    [
                        this.adjustParams,
                        {
                            key: 'revenue',
                            value: eurAmount,
                        },
                    ]
                );

                return true;
            }

            return false;
        },

        async openIframe({
            fiatAmount,
            chzAmount,
            fiatCurrency,
            deviceId,
        }) {
            const baseUrl = socios.paymentGatewayUrl;

            this.currentRate = Number(this.topupConversion.rate);
            this.currentRateDate = this.topupConversion.date;

            // Get code to send to FrontOffice in query parameters for authentication purposes
            const generatedCode = await this.getGeneratedCode();
            const encodedDate = this.currentRateDate.replace('+', '%2B');


            const queryParams = `&cryptoAmount=${chzAmount.original}
                &fiatAmount=${fiatAmount.original}
                &euroConversionRate=${this.euroConversionRate || 1}
                &fiatCurrency=${fiatCurrency.name}
                &fiatCurrencyId=${fiatCurrency.id}
                &generatedCode=${generatedCode}
                &userId=${this.userId ? this.userId : this.userDetails.externalUserId}
                &deviceUid=${deviceId}
                &deviceName=${this.device.name}
                &processStartedAt=${encodedDate}
                &locale=${this.locale}
                &webApp=true
                &appType=webapp
            `.replace(/\s/g, '');

            this.iframeSrc = `${baseUrl}/payments?${queryParams}`;
            this.isIframeShown = true;
            this.modalAmount = `${chzAmount.formatted} CHZ`;

            window.addEventListener('message', this.receiveMessage);

            this.$nextTick(() => {
                scrollToElement('payment-iframe');
            });
        },

        receiveMessage($event) {
            if ($event.data.analytics) {
                this.triggerAnalytics($event.data.analytics);
            }
            if ($event.data.confirmation) {

                if (this.isPartnerApp) {
                    this.$webTrackingInstance?.sendEvent(
                        EventNames.PartnerDeposit,
                        [
                            {
                                key: 'partner',
                                value: this.partnerAppData.id,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'amount',
                                value: this.modalAmount,
                            },
                        ]
                    );
                }

                this.setModal({
                    isVisible: true,
                    componentToLoad: 'components/payment-confirmation/payment-confirmation',
                    data: {
                        confirmation: $event.data.confirmation,
                        amount: this.modalAmount,
                    },
                });

                this.isIframeShown = false;
            }
        },

        triggerAnalytics(eventData) {
            const { eventName } = eventData;
            let chzAmount = this.cryptoValue;

            if (this.isTurned) {
                chzAmount = this.fiatValue;
            }
            const eurAmount = (chzAmount.formatted / this.eurRate).toFixed(2);

            switch (eventName) {
                // GA payment error
                case 'Topup_Change_amount_error':
                    this.$webTrackingInstance?.sendEvent(
                        eventName,
                        [
                            {
                                key: 'fiatAmount',
                                value: eventData.fiatAmount,
                            },
                            {
                                key: 'euroGrossAmount',
                                value: eurAmount,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'currency',
                                value: eventData.currency,
                            },
                        ]
                    );
                    break;
                // GA payment selection
                case 'Topup_Payment_Providers':
                    this.$webTrackingInstance?.sendEvent(
                        eventName,
                        [
                            {
                                key: 'name',
                                value: eventData.name,
                            },
                            {
                                key: 'cryptoTotalAmount',
                                value: eventData.cryptoTotalAmount,
                            },
                            {
                                key: 'euroGrossAmount',
                                value: eurAmount,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'currency',
                                value: eventData.currency,
                            },
                            {
                                key: 'name_pp',
                                value: eventData.provider,
                            },
                            {
                                key: 'serviceFee',
                                value: eventData.serviceFee,
                            },
                        ]
                    );
                    this.$webTrackingInstance?.sendEvent(
                        EventNames.TopupInitiatedPp,
                        [
                            this.adjustParams,
                            {
                                key: 'revenue',
                                value: eurAmount,
                            },
                        ]
                    );
                    break;
                // GA payment summary
                case 'Topup_Summary':
                    this.$webTrackingInstance?.sendEvent(
                        eventName,
                        [
                            {
                                key: 'name',
                                value: eventData.name,
                            },
                            {
                                key: 'cryptoTotalAmount',
                                value: eventData.cryptoTotalAmount,
                            },
                            {
                                key: 'euroGrossAmount',
                                value: eurAmount,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'currency',
                                value: eventData.currency,
                            },
                            {
                                key: 'serviceFee',
                                value: eventData.serviceFee,
                            },
                        ]
                    );
                    this.$webTrackingInstance?.sendEvent(
                        EventNames.TopupSummary,
                        [
                            this.adjustParams,
                            {
                                key: 'revenue',
                                value: eurAmount,
                            },
                            {
                                key: AdjustParameterKeys.SERVICE_FEE,
                                value: eventData.serviceFee,
                            },
                        ]
                    );
                    break;
                    // GA payment confirmation
                case 'Topup_Payment_Confirmation':
                    this.$webTrackingInstance?.sendEvent(
                        eventName,
                        [
                            {
                                key: 'fiatCurrency',
                                value: eventData.fiatCurrency,
                            },
                            {
                                key: 'euroGrossAmount',
                                value: eurAmount,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'referenceNumber',
                                value: eventData.referenceNumber,
                            },
                            {
                                key: 'device_id',
                                value: eventData.deviceId,
                            },
                        ]
                    );
                    this.$webTrackingInstance?.sendEvent(
                        EventNames.TopupCompleted,
                        [
                            this.adjustParams,
                            {
                                key: 'revenue',
                                value: eurAmount,
                            },
                        ]
                    );
                    break;
                    // GA payment rejection
                case 'Topup_Payment_Rejection':
                    this.$webTrackingInstance?.sendEvent(
                        eventName,
                        [
                            {
                                key: 'fiatCurrency',
                                value: eventData.fiatCurrency,
                            },
                            {
                                key: 'euroGrossAmount',
                                value: eurAmount,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'referenceNumber',
                                value: eventData.referenceNumber,
                            },
                            {
                                key: 'device_id',
                                value: eventData.deviceId,
                            },
                        ]
                    );
                    break;
                default: return null;
            }
        },

        modalCallback() {
            if (this.hasRestrictedPayments) {
                goBack(this);
            } else if (this.hasTopupGeoRestricted) {
                return this.$router.push('/');
            }
        },

        async setMalaysianDomesticRinggitBorrowingModal() {
            await askUserForFillRequirements({
                route: {
                    path: '/topup/',
                },
                requirements: [
                    {
                        id: RequirementId.MalaysiaDomesticRinggitBorrowing,
                        type: RequirementType.MalaysiaDomesticRinggitBorrowing,
                        config: null,
                        translations: [],
                    },
                ],
                onRequirementsFulfilled: () => ({}),
                onRejectedRequirements: () => ({}),
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.topup {
    position: relative;

    @include min-width(lg) {
        padding-top: 3rem;
    }

    &--disabled {
        &:hover {
            .topup__overlay {
                display: flex;
            }
        }

        .topup {
            &__inputs,
            &__middle-row {
                opacity: 0.5;
            }
        }
    }

    &__available-balance {
        color: $cyprus-color;
        font-size: 1.2rem;
        padding-bottom: 2rem;
        margin-top: -4rem;

        span:first-child {
            font-weight: bold;
        }
    }

    &__overlay {
        display: none;
        align-items: center;
        justify-content: center;
        color: $white-color;
        position: absolute;
        background: rgba($cyprus-color, 0.9);
        left: 2rem;
        top: 0;
        z-index: 2;
        width: calc(100% - 4rem);
        height: 100%;
        font-weight: bold;
        font-size: 1.8rem;
        border-radius: 1.5rem;
        cursor: pointer;

        i {
            font-size: 4.5rem;
            margin-right: 2rem;
            color: $lima-color;
        }
    }

    &__inputs {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        padding: 2.6rem 1.5rem;
        border-radius: 1.2rem;

        @include min-width(lg) {
            padding: 3.6rem 4.5rem;
        }

        &--dark {
            background: $athens-gray-color;
            padding-bottom: 4.4rem;

            @include min-width(md) {
                padding-bottom: 5rem;
            }

            @include max-width(xl) {
                padding-bottom: 6.4rem;
            }
        }
    }

    &__input-wrapper {
        position: relative;
        height: 100%;
        min-width: 5rem;
    }

    &__input {
        font: 700 2.6rem/2.6rem "Nunito";
        border: none;
        background: transparent;
        outline: none;
        text-align: right;
        position: relative;
        top: -0.2rem;
        max-width: 20rem;
        min-width: 5rem;
        appearance: textfield;
        text-overflow: ellipsis;
        box-shadow: none;
        @include input-color($cyprus-color);

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            appearance: none;
            margin: 0;
        }

        @include max-width(sm) {
            top: 0.2rem;
        }

        @include min-width(xl) {
            font-size: 3.6rem;
            line-height: 3.6rem;
            top: 0.5rem;
        }

        &--invalid {
            @include input-color($torch-red-color);
        }
    }

    &__middle-row {
        display: flex;
        padding: 0 4rem;
        justify-content: space-between;
        align-items: center;
        margin: -1.6rem 0 0.6rem;
        z-index: 1;

        @include min-width(xl) {
            padding: 0 8.6rem;
        }

        @include max-width(sm) {
            padding: 0 2rem;
        }
    }
}

.daily-limit-reached {
    position: absolute;
    right: 0;
    bottom: -2.5rem;

    @include min-width(lg) {
        bottom: -2.8rem;
    }

    &__text {
        font-size: 1.2rem;
        margin-right: 1rem;
        color: $torch-red-color;

        @include min-width(lg) {
            font-size: 1.4rem;
        }
    }
}

.icon {
    background: $white-color;
    border-radius: 50%;
    cursor: pointer;

    &-exchange {
        font-size: 3.2rem;
        color: $lima-color;
        transition: all 0.1s ease;

        &--twirl {
            position: relative;
            top: -0.1rem;
            left: -0.1rem;
            transform: rotate(180deg);
        }
    }

    &-info {
        font-size: 2.2rem;
        color: $cornflower-blue-color;

        @include min-width(lg) {
            font-size: 2.4rem;
        }
    }
}

.wrapper {
    padding-bottom: 8rem;
}

iframe {
    width: 100%;
    min-height: 77vh;

    @include min-width(xl) {
        width: 55%;
        margin: 0 auto;
    }
}
</style>